import { FilterOutlined } from '@ant-design/icons'
import { Badge } from 'antd'
import { FC, useMemo } from 'react'
import { createPortal } from 'react-dom'

import FeedSearchTerm from '@/components/Feed/FeedSearchTerm/FeedSearchTerm'
import SelectedFilters from '@/components/Filters/SelectedFilters/SelectedFilters'
import useSearch from '@/hooks/search/useSearch'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { CharacteristicFilterWithRange } from '@/types'

import Loader from '../../../components/Atoms/Loader/Loader'
import ResetFiltersButton from '../../../components/Filters/ResetFiltersButton/ResetFiltersButton'
import { useMainOutletContext } from '../../Main/mainOutletContext'
import cn from '../Feed.module.scss'
import FeedFilterActions from '../FeedFilterActions/FeedFilterActions'

interface FeedSectionProps {
  setOpenFilters: (open: boolean) => void
  dataFetchingError?: string
}

const FeedSection: FC<FeedSectionProps> = ({
  setOpenFilters,
  dataFetchingError,
}) => {
  const { products, productsLoading, totalElements } = useFeedStore(
    (state) => state
  )
  const search = useSearch()
  const outletContext = useMainOutletContext()
  const filterActionsSlot = outletContext?.filterActionsSlot ?? null
  const priorityFiltersSlot = outletContext?.priorityFiltersSlot ?? null

  const {
    searchTerm,
    currentlySearchableBrands,
    currentlySearchableCategories,
    currentlySearchableProductCodes,
    currentlySearchableStores,
    currentlySearchableCharacteristics,
    withLeftovers,
    searchError,
    initialFilters,
    availableFilters,
  } = useSearchStore((state) => state)

  const popularCodes = useMemo(() => {
    const popular =
      availableFilters?.characteristics?.filter((c) => c.isMain === true) || []
    return new Set(popular.map((c) => c.code))
  }, [availableFilters])

  const isAnythingSelected = useMemo(() => {
    return [
      withLeftovers,
      currentlySearchableBrands.length > 0,
      currentlySearchableCategories.length > 0,
      currentlySearchableProductCodes.length > 0,
      currentlySearchableStores.length > 0,
      currentlySearchableCharacteristics.length > 0,
    ].some(Boolean)
  }, [
    withLeftovers,
    currentlySearchableBrands,
    currentlySearchableCategories,
    currentlySearchableProductCodes,
    currentlySearchableStores,
    currentlySearchableCharacteristics,
  ])

  const hasDivergedFromInitial = useMemo(() => {
    if (!initialFilters) return isAnythingSelected

    const compareIds = (arr1: { id: string }[], arr2: { id: string }[]) => {
      const ids1 = (arr1 || [])
        .map((i) => i.id)
        .sort()
        .join(',')
      const ids2 = (arr2 || [])
        .map((i) => i.id)
        .sort()
        .join(',')
      return ids1 !== ids2
    }

    if (searchTerm.trim() !== initialFilters.searchTerm.trim()) return true
    if (withLeftovers !== false) return true
    if (currentlySearchableCharacteristics.length > 0) return true
    if (currentlySearchableStores.length > 0) return true
    if (compareIds(currentlySearchableBrands, initialFilters.brands))
      return true
    if (compareIds(currentlySearchableCategories, initialFilters.categories))
      return true

    const currentCodes = [...currentlySearchableProductCodes].sort().join(',')
    const initialCodes = [...(initialFilters.productCodes || [])]
      .sort()
      .join(',')
    if (currentCodes !== initialCodes) return true

    return false
  }, [
    searchTerm,
    currentlySearchableBrands,
    currentlySearchableCategories,
    currentlySearchableProductCodes,
    currentlySearchableCharacteristics,
    currentlySearchableStores,
    withLeftovers,
    initialFilters,
    isAnythingSelected,
  ])

  const handleResetFilters = () => {
    search({
      query: initialFilters?.searchTerm ?? '',
      categories: initialFilters?.categories ?? [],
      brands: initialFilters?.brands ?? [],
      productCodes: initialFilters?.productCodes ?? [],
      characteristicFilters: [],
      stores: [],
      page: 0,
      withLeftovers: false,
      resetFilters: true,
      triggerNavigation: true,
    })
  }

  const { selectedOrganisation } = useUserInfoStore((state) => state)

  const handleToggleLeftovers = () => {
    const newValue = !withLeftovers
    search({
      query: searchTerm,
      categories: currentlySearchableCategories,
      brands: currentlySearchableBrands,
      productCodes: currentlySearchableProductCodes,
      characteristicFilters: currentlySearchableCharacteristics,
      stores: currentlySearchableStores,
      page: 0,
      withLeftovers: newValue,
      triggerNavigation: true,
    })
  }

  const filtersCount = currentlySearchableCharacteristics
    .filter((c) => !popularCodes.has(c.code))
    .reduce((acc, c) => {
      const charWithRange = c as CharacteristicFilterWithRange
      const valuesCount =
        (c.options?.length || 0) + (c.chosenOptions?.length || 0)
      const rangeCount =
        charWithRange.chosenRange?.min != null ||
        charWithRange.chosenRange?.max != null
          ? 1
          : 0
      return acc + valuesCount + rangeCount
    }, 0)

  const isOrganisationSelected = Object.keys(selectedOrganisation).length > 0

  if (!products) return null

  return (
    <section className={cn.feed}>
      {filterActionsSlot &&
        createPortal(
          <FeedFilterActions
            filtersCount={filtersCount}
            totalCount={totalElements}
            loading={productsLoading}
            canReset={hasDivergedFromInitial}
            withLeftovers={withLeftovers}
            organisationSelected={isOrganisationSelected}
            onOpenFilters={() => setOpenFilters(true)}
            onReset={handleResetFilters}
            onToggleLeftovers={handleToggleLeftovers}
          />,
          filterActionsSlot
        )}
      <div className={cn.topRow}>
        {currentlySearchableStores.length &&
        !currentlySearchableBrands.length &&
        !currentlySearchableCategories.length &&
        !searchTerm ? (
          <></>
        ) : (
          <FeedSearchTerm />
        )}
      </div>

      <div className={cn.feedContent}>
        <div className={cn.layoutSplitter}>
          <div className={cn.leftSide}>
            <div className={cn.selectedFiltersWrapper}>
              <SelectedFilters popularTarget={priorityFiltersSlot} />
            </div>
          </div>

          {!filterActionsSlot && (
            <div className={cn.rightSideColumn}>
              <div
                className={[
                  cn.controlItem,
                  Object.keys(selectedOrganisation).length
                    ? cn.organisationSelected
                    : '',
                ].join(' ')}
              >
                <div className={cn.filtersControlRow}>
                  <Badge
                    count={filtersCount}
                    classNames={{ indicator: cn.filtersCountBadge }}
                  >
                    <button
                      className={`${cn.filterBtn} ${cn.filterBtnFullWidth}`}
                      onClick={() => setOpenFilters(true)}
                    >
                      <div className={cn.filterOutlinedContainer}>
                        <FilterOutlined />
                      </div>
                      Фильтры
                    </button>
                  </Badge>
                  <ResetFiltersButton
                    selectedOrganisation={selectedOrganisation}
                    onClick={handleResetFilters}
                    disabled={!hasDivergedFromInitial}
                  />
                </div>
              </div>

              <div className={cn.controlItem}>
                <button
                  className={[
                    cn.stockToggleBtn,
                    withLeftovers ? cn.active : '',
                    Object.keys(selectedOrganisation).length
                      ? cn.organisationSelected
                      : '',
                  ].join(' ')}
                  onClick={handleToggleLeftovers}
                >
                  <div className={cn.indicatorContainer}>
                    <div className={cn.indicatorCircle}></div>
                    <div className={cn.indicatorPulse}></div>
                  </div>
                  <div className={cn.btnTextBlock}>
                    <div className={cn.btnText}>Оставить в наличии</div>
                    <div className={cn.btnSubText}>
                      {productsLoading ? (
                        <Loader size={4} />
                      ) : (
                        `Найдено: ${totalElements}`
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        {!productsLoading &&
          !products.length &&
          !searchError &&
          !dataFetchingError && (
            <div
              className={
                Object.keys(selectedOrganisation).length
                  ? [
                      cn.emptyOrErrorMessageContainer,
                      cn.organisationSelected,
                    ].join(' ')
                  : cn.emptyOrErrorMessageContainer
              }
            >
              <span>Совпадений не найдено</span>
            </div>
          )}

        {searchError && (
          <div
            className={[
              cn.emptyOrErrorMessageContainer,
              Object.keys(selectedOrganisation).length > 0
                ? cn.organisationSelected
                : '',
              typeof searchError === 'object' ? cn.detailedError : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {typeof searchError === 'object' && searchError !== null ? (
              <>
                <div className={cn.errorMainMessage}>{searchError.message}</div>
                {searchError.details && searchError.details.length > 0 && (
                  <div className={cn.errorDetailsBlock}>
                    <div className={cn.errorDetailsTitle}>Подробности:</div>
                    {searchError.details.map((detail, index) => (
                      <div key={index} className={cn.errorDetailItem}>
                        <div className={cn.detailText}>{detail.message}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <span className={cn.searchErrorText}>{searchError}</span>
            )}
          </div>
        )}

        {dataFetchingError && (
          <div className={cn.emptyOrErrorMessageContainer}>
            <span>{dataFetchingError}</span>
          </div>
        )}
      </div>
    </section>
  )
}

export default FeedSection
